import type { DockPanelKey } from "../components/dockPanelLabels";

/** Dock panels whose heavy refresh work must stay off the playback hot path. */
export const DOCK_PANEL_PLAYBACK_REFRESH_PANELS = [
  "event_detection",
  "qeeg_trends",
  "clinical_collab",
  "spectrogram",
  "psd_analysis",
] as const satisfies readonly DockPanelKey[];

export type DockPanelPlaybackRefreshKey =
  (typeof DOCK_PANEL_PLAYBACK_REFRESH_PANELS)[number];

export const DEFAULT_DOCK_PANEL_PLAYBACK_REFRESH_INTERVAL_MS = 30_000;
export const DEFAULT_DOCK_PANEL_PLAYBACK_STAGGER_OFFSET_MS = 3_000;

export type DockPanelPlaybackRefreshSchedulerOptions = {
  isActive: () => boolean;
  isPanelVisible: (panelKey: DockPanelPlaybackRefreshKey) => boolean;
  readPlayheadSeconds: () => number;
  onPublish: (
    panelKey: DockPanelPlaybackRefreshKey,
    playheadSeconds: number,
  ) => void;
  refreshIntervalMs?: number;
  staggerOffsetMs?: number;
};

type PanelTimerHandles = {
  startTimeoutId: ReturnType<typeof setTimeout>;
  intervalId: ReturnType<typeof setInterval> | null;
};

/**
 * Lightweight staggered refresh scheduler for dock analysis panels during
 * playback. Each visible panel publishes on its own phase offset, and publishes
 * are drained one-at-a-time through idle callbacks so AI/Trends/Collab/
 * Spectrogram/PSD do not stampede the main thread on the same timestamp.
 */
export function createDockPanelPlaybackRefreshScheduler(
  options: DockPanelPlaybackRefreshSchedulerOptions,
): { dispose: () => void; syncVisiblePanels: () => void } {
  const refreshIntervalMs =
    options.refreshIntervalMs ?? DEFAULT_DOCK_PANEL_PLAYBACK_REFRESH_INTERVAL_MS;
  const staggerOffsetMs =
    options.staggerOffsetMs ?? DEFAULT_DOCK_PANEL_PLAYBACK_STAGGER_OFFSET_MS;

  const panelTimers = new Map<
    DockPanelPlaybackRefreshKey,
    PanelTimerHandles
  >();
  const publishQueue: DockPanelPlaybackRefreshKey[] = [];
  const queuedKeys = new Set<DockPanelPlaybackRefreshKey>();
  let idleDrainHandle: number | null = null;
  let idleDrainTimeoutId: ReturnType<typeof setTimeout> | null = null;

  const clearIdleDrain = () => {
    if (
      idleDrainHandle !== null &&
      typeof cancelIdleCallback !== "undefined"
    ) {
      cancelIdleCallback(idleDrainHandle);
      idleDrainHandle = null;
    }
    if (idleDrainTimeoutId !== null) {
      clearTimeout(idleDrainTimeoutId);
      idleDrainTimeoutId = null;
    }
  };

  const drainPublishQueue = () => {
    idleDrainHandle = null;
    idleDrainTimeoutId = null;
    if (!options.isActive()) {
      publishQueue.length = 0;
      queuedKeys.clear();
      return;
    }

    const panelKey = publishQueue.shift();
    if (!panelKey) {
      queuedKeys.clear();
      return;
    }
    queuedKeys.delete(panelKey);

    if (options.isPanelVisible(panelKey)) {
      options.onPublish(panelKey, options.readPlayheadSeconds());
    }

    if (publishQueue.length > 0) {
      schedulePublishDrain();
    }
  };

  const schedulePublishDrain = () => {
    if (idleDrainHandle !== null || idleDrainTimeoutId !== null) {
      return;
    }
    if (typeof requestIdleCallback === "function") {
      let idleHandleStillPending = true;
      const handle = requestIdleCallback(() => {
        idleDrainHandle = null;
        idleHandleStillPending = false;
        drainPublishQueue();
      }, { timeout: 500 });
      if (idleHandleStillPending) {
        idleDrainHandle = handle;
      }
      return;
    }
    let timeoutHandleStillPending = true;
    const timeoutId = setTimeout(() => {
      idleDrainTimeoutId = null;
      timeoutHandleStillPending = false;
      drainPublishQueue();
    }, 0);
    if (timeoutHandleStillPending) {
      idleDrainTimeoutId = timeoutId;
    }
  };

  const enqueuePublish = (panelKey: DockPanelPlaybackRefreshKey) => {
    if (!options.isActive() || !options.isPanelVisible(panelKey)) {
      return;
    }
    if (queuedKeys.has(panelKey)) {
      return;
    }
    queuedKeys.add(panelKey);
    publishQueue.push(panelKey);
    schedulePublishDrain();
  };

  const clearPanelTimer = (panelKey: DockPanelPlaybackRefreshKey) => {
    const handles = panelTimers.get(panelKey);
    if (!handles) {
      return;
    }
    clearTimeout(handles.startTimeoutId);
    if (handles.intervalId !== null) {
      clearInterval(handles.intervalId);
    }
    panelTimers.delete(panelKey);
  };

  const startPanelTimer = (panelKey: DockPanelPlaybackRefreshKey) => {
    clearPanelTimer(panelKey);
    if (!options.isActive() || !options.isPanelVisible(panelKey)) {
      return;
    }

    const panelIndex = DOCK_PANEL_PLAYBACK_REFRESH_PANELS.indexOf(panelKey);
    const phaseDelayMs = Math.max(0, panelIndex) * staggerOffsetMs;

    const startTimeoutId = setTimeout(() => {
      enqueuePublish(panelKey);
      const intervalId = setInterval(
        () => enqueuePublish(panelKey),
        refreshIntervalMs,
      );
      panelTimers.set(panelKey, { startTimeoutId, intervalId });
    }, phaseDelayMs);

    panelTimers.set(panelKey, { startTimeoutId, intervalId: null });
  };

  const syncVisiblePanels = () => {
    if (!options.isActive()) {
      for (const panelKey of DOCK_PANEL_PLAYBACK_REFRESH_PANELS) {
        clearPanelTimer(panelKey);
      }
      clearIdleDrain();
      publishQueue.length = 0;
      queuedKeys.clear();
      return;
    }

    for (const panelKey of DOCK_PANEL_PLAYBACK_REFRESH_PANELS) {
      if (options.isPanelVisible(panelKey)) {
        if (!panelTimers.has(panelKey)) {
          startPanelTimer(panelKey);
        }
      } else {
        clearPanelTimer(panelKey);
        queuedKeys.delete(panelKey);
        const queueIndex = publishQueue.indexOf(panelKey);
        if (queueIndex >= 0) {
          publishQueue.splice(queueIndex, 1);
        }
      }
    }
  };

  const dispose = () => {
    for (const panelKey of DOCK_PANEL_PLAYBACK_REFRESH_PANELS) {
      clearPanelTimer(panelKey);
    }
    clearIdleDrain();
    publishQueue.length = 0;
    queuedKeys.clear();
  };

  return { dispose, syncVisiblePanels };
}
